import React, { useState, useEffect, useRef, useCallback } from "react"
import { Modal, message, Tooltip, Checkbox, Input } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import { useEditor, EditorContent, Editor } from "@tiptap/react"
import Document from "@tiptap/extension-document"
import Text from "@tiptap/extension-text"
import Paragraph from "@tiptap/extension-paragraph"
import SmartClipboardExtension from "shared/TipTap/Extensions/SmartClipboardExtension"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { useEngine } from "hooks/api/useEngine"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faFolder,
  faInfoCircle,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons"
import { Tree } from "antd"
import type { CheckboxChangeEvent } from "antd/es/checkbox"

import { DownOutlined } from "@ant-design/icons"

import { escapeHTML } from "utils/escapeHtml"
import {
  filterCodebookTree,
  getCodebookNodePath,
  transformCodebookToTreeData,
} from "utils/codebookUtils"
import { Loader } from "shared/ui/Loader/Loader.component"
import { CodebookData, AntTreeNode } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import { EngineQueryResult } from "types/engineOutput.types"
import { AxiosResponse } from "axios"

const { Search } = Input

// Interface for tree node data (extending the existing AntTreeNode from codebook.types)

// Interface for tree selection info
interface TreeSelectInfo {
  node: AntTreeNode
  selectedNodes: AntTreeNode[]
  nativeEvent: MouseEvent
}

// Every node gets the same folder icon, applied via the Tree-level `icon`
// prop (the shared transform deliberately emits undecorated nodes).
const treeNodeIcon = <FontAwesomeIcon icon={faFolder} color="#36c3ed" />

// Props interface for the component
interface CustomQueryModalProps {
  /** Whether the modal is visible */
  isVisible: boolean
  /** Function to close the modal */
  onClose: () => void
  /** Query instance for engine operations */
  queryInstance: QueryInstance | null
  /** Callback when script runs successfully */
  onScriptRunSuccess?: () => void
  /** Function to show the custom query modal */
  showCustomQueryModal: () => void
  /** Whether auto-run is enabled */
  isAutoRunEnabled: boolean
  /** Callback when auto-run setting changes */
  onAutoRunChange: (enabled: boolean) => void
  /** Initial script content */
  initialScriptContent: string
  /** Callback to save script content */
  onScriptContentSave?: (script: string) => void
  /** Codebook data for tree display */
  codebook: CodebookData | null
}

// Type for tab keys
type TabKey = "editor" | "help"

const editorStyles = `
  .modal-split-view {
    display: flex;
    gap: 8px;
    height: 100%;
  }

  .modal-left-panel, .modal-right-panel {
    display: flex;
    flex-direction: column;
  }

  .modal-left-panel {
    width: 230px;
  }

  .modal-right-panel {
    flex: 1;
  }

  .tab-container {
    display: flex;
    border-bottom: 1px solid #e8e8e8;
    margin-bottom: 4px;
  }

  .tab {
    padding: 0 10px;
    cursor: pointer;
    font-size: 12px;
    line-height: 24px;
    color: #666;
  }

  .tab.active {
    color: #36C3ED;
    border-bottom: 2px solid #36C3ED;
    font-weight: 500;
  }

  .editor-container {
    position: relative;
    display: flex;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    height: 296px;
    overflow: auto;
    background-color: #fff;
  }

  .line-numbers {
    position: absolute;
    left: 0;
    top: 0;
    width: 32px;
    background-color: #f0f0f0;
    border-right: 1px solid #e0e0e0;
    font-family: monospace;
    font-size: 11px;
    color: #666;
    user-select: none;
    pointer-events: none;
  }

  .line-number {
    position: absolute;
    padding-right: 4px;
    width: 28px;
    text-align: right;
    font-size: 11px;
    font-weight: normal;
    line-height: 18px;
  }

  .editor-wrapper {
    width: 100%;
  }

  .tiptap {
    padding-top: 3px;
    padding-bottom: 8px;
    padding-right: 8px;
    width: 100%;
    min-height: calc(296px - 16px);
    border: none;
    font-family: monospace;
    font-size: 12px;
    line-height: 18px;
    background-color: transparent;
  }

  .tiptap .ProseMirror {
    outline: none;
    min-height: calc(296px - 16px);
  }

  .tiptap:focus {
    outline: none;
  }

  .tiptap p {
    margin: 0;
    line-height: 18px;
    min-height: 18px;
  }

  /* Ensure line numbers container can scroll with content */
  .line-numbers {
    width: 32px;
    flex-shrink: 0;
    background-color: #f0f0f0;
    border-right: 1px solid #e0e0e0;
    text-align: right;
    padding: 0;
    padding-top: 8px;
    font-family: monospace;
    font-size: 11px;
    color: #666;
    user-select: none;
    position: relative;
    overflow: hidden;
  }

  .compact-tree {
    height: 296px;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .compact-tree .ant-tree {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0;
    margin: 0;
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  .compact-tree .ant-tree::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  .compact-tree .ant-tree {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  /* Adjust tree node appearance */
  .compact-tree .ant-tree-list {
    height: 100% !important;
  }

  .compact-tree .ant-tree-treenode {
    padding: 0 !important;
    line-height: 18px !important;
  }

  .compact-tree .ant-tree-node-content-wrapper {
    min-height: 18px !important;
    line-height: 18px !important;
    padding: 0 2px !important;
  }

  .compact-tree .ant-tree-title {
    font-size: 11px;
  }

  .compact-tree .ant-tree-switcher {
    width: 16px !important;
    line-height: 18px !important;
    height: 18px !important;
  }

  .compact-tree .ant-tree-indent-unit {
    width: 12px !important;
  }

  /* Custom minimal scrollbar for editor */
  .tiptap::-webkit-scrollbar {
    width: 6px;
  }

  .tiptap::-webkit-scrollbar-track {
    background: transparent;
  }

  .tiptap::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 3px;
  }

  .tiptap::-webkit-scrollbar-thumb:hover {
    background: #bbb;
  }

  .help-content {
    height: 296px;
    overflow-y: auto;
  }

  /* Modal title search box styling */
  .modal-title-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .modal-title-search {
    width: 180px;
  }

  /* Loading state styles */
  .editor-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    background-color: #f9f9f9;
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    flex-direction: column;
    gap: 12px;
  }

  .loading-text {
    font-size: 12px;
    color: #666;
  }
`

const CustomQueryModal: React.FC<CustomQueryModalProps> = ({
  isVisible,
  onClose,
  queryInstance,
  onScriptRunSuccess,
  showCustomQueryModal,
  isAutoRunEnabled,
  onAutoRunChange,
  initialScriptContent,
  onScriptContentSave,
  codebook,
}) => {
  const { RunEngineQuery } = useEngine()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [treeData, setTreeData] = useState<AntTreeNode[]>([])
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [tabActiveKey, setTabActiveKey] = useState<TabKey>("editor")
  const [isEditorReady, setIsEditorReady] = useState<boolean>(false)

  const editorWrapperRef = useRef<HTMLDivElement>(null)
  const lineNumbersRef = useRef<HTMLDivElement>(null)

  // Helper to format plain text into HTML paragraphs for TipTap
  const formatTextForEditor = (text: string): string => {
    if (!text) return ""
    return text
      .split("\n")
      .map((line: string) => `<p>${escapeHTML(line)}</p>`)
      .join("")
  }

  const editor: Editor | null = useEditor({
    extensions: [Document, Text, Paragraph, SmartClipboardExtension],
    content: formatTextForEditor(initialScriptContent || ""),
    editorProps: {
      attributes: {
        class: "prose prose-sm focus:outline-none",
      },
    },
  })

  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false)

  // Handle window resize
  useEffect(() => {
    const handleResize = (): void => {
      const width: number = window.innerWidth
      const smallScreenBreakpoint: number = 1400
      const newIsSmallScreen: boolean = width < smallScreenBreakpoint
      setIsSmallScreen(newIsSmallScreen)
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [isSmallScreen])

  // Transform codebook into tree data
  useEffect(() => {
    if (codebook) {
      const data: AntTreeNode[] = transformCodebookToTreeData(codebook)
      setTreeData(data)
    }
  }, [codebook])

  // Update editor content when initialScriptContent changes
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      const formattedContent: string = formatTextForEditor(
        initialScriptContent || ""
      )
      if (editor.getHTML() !== formattedContent) {
        editor.commands.setContent(formattedContent)
      }
    }
  }, [editor, initialScriptContent])

  // Filter tree data based on search term
  const filteredTreeData = React.useMemo(
    (): AntTreeNode[] => filterCodebookTree(treeData, searchTerm),
    [treeData, searchTerm]
  )

  // Check if editor is fully initialized and ready
  const checkEditorReady = useCallback((): boolean => {
    if (!editor || !isVisible || tabActiveKey !== "editor") {
      setIsEditorReady(false)
      return false
    }

    const editorScrollContainer = editorWrapperRef.current
    if (!editorScrollContainer) {
      setIsEditorReady(false)
      return false
    }

    // Check if editor has proper dimensions
    const rect: DOMRect = editorScrollContainer.getBoundingClientRect()
    const hasValidDimensions = rect.width > 0 && rect.height > 0
    const hasValidView = editor.view && editor.view.dom
    const isReady: boolean = hasValidDimensions && !!hasValidView

    setIsEditorReady(isReady)
    return isReady
  }, [editor, isVisible, tabActiveKey])

  // Effect to monitor editor readiness
  useEffect(() => {
    if (!editor || !isVisible) {
      setIsEditorReady(false)
      return
    }

    if (tabActiveKey === "editor") {
      // Check immediately
      const immediate: boolean = checkEditorReady()

      if (!immediate) {
        // Poll until ready
        let attempts: number = 0
        const maxAttempts: number = 30

        const pollReady = (): void => {
          attempts++
          const ready: boolean = checkEditorReady()

          if (!ready && attempts < maxAttempts) {
            setTimeout(pollReady, 100)
          }
        }

        setTimeout(pollReady, 50)
      }

      // Fallback timeout - show editor even if checks fail
      const fallbackTimeout: NodeJS.Timeout = setTimeout(() => {
        if (!isEditorReady) {
          console.warn("Editor readiness timeout, showing anyway")
          setIsEditorReady(true)
        }
      }, 3000) // 3 second fallback

      return () => clearTimeout(fallbackTimeout)
    } else {
      setIsEditorReady(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, isVisible, tabActiveKey, checkEditorReady])

  // Simplified line numbers effect - only runs when editor is ready
  useEffect(() => {
    if (!isEditorReady || !editor) return

    const lineNumbersContainer = lineNumbersRef.current
    const editorScrollContainer = editorWrapperRef.current

    if (!lineNumbersContainer || !editorScrollContainer) return

    // Function to render line numbers (simplified, non-virtualized for now)
    const renderLineNumbers = (): void => {
      try {
        const lines: NodeListOf<Element> =
          editor.view.dom.querySelectorAll(".ProseMirror > *")

        // Clear existing line numbers
        lineNumbersContainer.innerHTML = ""

        // Handle empty editor case
        if (lines.length === 0) {
          const lineNumber: HTMLDivElement = document.createElement("div")
          lineNumber.className = "line-number"
          lineNumber.textContent = "1"
          lineNumber.style.top = "5.5px"
          lineNumber.style.height = "18px"
          lineNumbersContainer.appendChild(lineNumber)
          // Set minimum height for empty editor
          lineNumbersContainer.style.height = "295px"
          return
        }

        // Render line numbers for all lines (not virtualized)
        let currentTop: number = 5.5 // Account for padding-top
        Array.from(lines).forEach((line: Element, index: number) => {
          const lineNumber: HTMLDivElement = document.createElement("div")
          lineNumber.className = "line-number"
          lineNumber.textContent = String(index + 1)
          lineNumber.style.top = `${currentTop}px`
          lineNumber.style.height = `${
            (line as HTMLElement).offsetHeight || 18
          }px`
          lineNumbersContainer.appendChild(lineNumber)
          currentTop += (line as HTMLElement).offsetHeight || 18
        })

        // Make sure line numbers container height matches content + padding
        const totalHeight: number = Math.max(currentTop + 8, 293) // Ensure minimum 293px height
        lineNumbersContainer.style.height = `${totalHeight}px`
      } catch (error) {
        console.error("Error rendering line numbers:", error)
      }
    }

    // Initial render
    renderLineNumbers()

    // Event listeners
    const handleUpdate = renderLineNumbers
    const handleScroll = (): void => {
      // Line numbers are absolutely positioned, so they scroll automatically with the container
      // No need to do anything special here
    }

    editor.on("update", handleUpdate)
    editorScrollContainer.addEventListener("scroll", handleScroll)

    return () => {
      editor.off("update", handleUpdate)
      editorScrollContainer.removeEventListener("scroll", handleScroll)
    }
  }, [isEditorReady, editor])

  // Handle search input change
  const handleSearch = (value: string): void => {
    setSearchTerm(value)
  }

  // Tree node selection handler
  const onSelect = (selectedKeys: React.Key[], info: TreeSelectInfo): void => {
    const { node } = info
    const { key } = node
    const path: string[] = getCodebookNodePath(treeData, key as string)
    const insertContent: string =
      path.length > 1 ? `<<${path.join("//")}>> ` : `<<${path[0]}>> `

    if (editor) {
      setTabActiveKey("editor")
      editor.commands.focus()
      editor.commands.insertContent(insertContent)
    }
  }

  const handleRunScript = async (): Promise<void> => {
    if (!editor || !queryInstance) {
      notify.toast.error("Editor or Query Instance not available.")
      return
    }

    let scriptContent: string = ""
    scriptContent += editor.getText().replace(/\n\n+/g, "\n")

    if (!scriptContent.trim()) {
      notify.toast.warning("Script cannot be empty.")
      return
    }
    setIsLoading(true)
    // keyed message flow — notify.toast has no replace-in-place; keep antd message here
    message.loading({
      content: "Running custom script...",
      key: "customScript",
    })
    try {
      const result: AxiosResponse<unknown> = await RunEngineQuery(
        queryInstance,
        "RESET\n" + scriptContent,
        false
      )

      const engineResult = result.data as EngineQueryResult

      if (
        result?.status === 200 &&
        engineResult?.result?.log?.[0] === "Script successfully executed."
      ) {
        message.success({
          content: "Custom script executed successfully!",
          key: "customScript",
          duration: 2,
        })
        if (onScriptContentSave) {
          onScriptContentSave(scriptContent)
        }
        if (onScriptRunSuccess) onScriptRunSuccess()
        onClose()
      } else {
        const engineError: string =
          engineResult?.result?.output?.[0]?.message || "Unknown engine error"
        const statusText: string = result?.status
          ? ` (Status: ${result.status})`
          : ""
        message.error({
          content: `Script execution failed: ${engineError}${statusText}`,
          key: "customScript",
          duration: 5,
        })
        console.error("Custom script error details:", result)
      }
    } catch (error) {
      const errorMessage: string =
        error instanceof Error ? error.message : String(error)
      console.error("Error running custom script:", error)
      message.error({
        content: `Error running script: ${errorMessage}`,
        key: "customScript",
        duration: 4,
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Handle checkbox change
  const handleCheckboxChange = (e: CheckboxChangeEvent): void => {
    if (onAutoRunChange) {
      onAutoRunChange(e.target.checked)
    }
  }

  // Destroy editor on unmount
  useEffect(() => {
    return () => {
      if (editor && !editor.isDestroyed) {
        editor.destroy()
      }
    }
  }, [editor])

  return (
    <>
      <div className="inline-block relative">
        <div className="bg-white px-3 py-2 rounded-b-lg shadow-sm">
          <Tooltip title="Run Custom Script">
            <DataQButton
              onClick={showCustomQueryModal}
              type="primary"
              size="small"
              isDisabled={!queryInstance}
            >
              {isSmallScreen ? (
                <FontAwesomeIcon icon={faSpinner} />
              ) : (
                "Run Custom Script"
              )}
            </DataQButton>
          </Tooltip>
        </div>
      </div>

      <Modal
        title="Run Custom Preprocessing Script"
        open={isVisible}
        onCancel={onClose}
        width={900}
        footer={[
          <DataQButton key="back" onClick={onClose}>
            Cancel
          </DataQButton>,
          <DataQButton
            key="submit"
            type="primary"
            isLoading={isLoading}
            onClick={handleRunScript}
            isDisabled={!editor || isLoading || !queryInstance}
          >
            Run & Save Script
          </DataQButton>,
        ]}
        destroyOnHidden={true}
      >
        <style>{editorStyles}</style>
        <p className="mb-2 text-sm text-gray-600">
          Enter a script to run against the current engine instance. Use the
          Data Tree to select variables.
        </p>

        <div className="modal-split-view">
          <div className="modal-left-panel">
            <div style={{ marginBottom: "4px" }}>
              <Search
                placeholder="Search Tree"
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: "100%" }}
                size="small"
              />
            </div>
            <div className="compact-tree">
              {treeData.length > 0 ? (
                <Tree
                  showIcon
                  icon={treeNodeIcon}
                  treeData={filteredTreeData}
                  onSelect={onSelect}
                  switcherIcon={<DownOutlined style={{ fontSize: "10px" }} />}
                  blockNode
                  virtual
                  motion={false}
                  height={296}
                  style={{
                    height: "100%",
                    padding: 0,
                  }}
                />
              ) : (
                <div className="p-3 text-center text-gray-400">
                  <p className="text-xs">Codebook not available</p>
                </div>
              )}
            </div>
          </div>

          <div className="modal-right-panel">
            <div className="tab-container">
              <div
                className={`tab ${tabActiveKey === "editor" ? "active" : ""}`}
                onClick={() => setTabActiveKey("editor")}
              >
                Editor
              </div>
              <div
                className={`tab ${tabActiveKey === "help" ? "active" : ""}`}
                onClick={() => setTabActiveKey("help")}
              >
                Help
              </div>
            </div>

            {tabActiveKey === "editor" ? (
              !isEditorReady ? (
                <div className="editor-loading">
                  <Loader fontSize={24} color="#36C3ED" />
                  <span className="loading-text">Loading editor...</span>
                </div>
              ) : (
                <div className="editor-container" ref={editorWrapperRef}>
                  <div className="line-numbers" ref={lineNumbersRef}></div>
                  <div className="editor-wrapper">
                    {editor && <EditorContent editor={editor} />}
                  </div>
                </div>
              )
            ) : (
              <div className="help-content p-2 border rounded bg-gray-50">
                <h4 className="text-xs font-medium m-0">Script Help</h4>
                <ul className="text-xs mt-1 space-y-1 list-disc pl-4">
                  <li>Click items in the Data Tree to insert variables</li>
                  <li>
                    Variables use the syntax <code>{"<<variable//path>>"}</code>
                  </li>
                  <li>
                    See documentation on query language and syntax{" "}
                    <a
                      href=""
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 underline"
                    >
                      here
                    </a>
                  </li>
                  <li>
                    Don&apos;t define Tables/Charts here as they won&apos;t be
                    displayed.
                  </li>
                </ul>
                <div className="mt-2 border-t pt-1">
                  <h5 className="text-xs font-medium m-0">Example:</h5>
                  <pre className="text-xs bg-gray-100 p-1 mt-1 rounded overflow-x-auto">
                    {`# In this script, We apply weights using the USEWEIGHTS command.
# We remove the occupation variable "None of the Above".
# We create a new variable called MyRegion based on the Region variable.
# We assign the value of MyRegion to be true if the Region is Sydney or Melbourne.

USEWEIGHTS <<Weight>>
REMOVE <<Occupation//None of the Above>>

QTABLE MyRegion
FIELD <<MyRegion//NSW>> BOOL false
FIELD <<MyRegion//VIC>> BOOL false
FIELD True BOOL true
ASSIGN <<MyRegion//NSW>> True IF:<<Region//Sydney>>
ASSIGN <<MyRegion//NSW>> True IF:<<Region//Other NSW>>
ASSIGN <<MyRegion//VIC>> True IF:<<Region//Melbourne>>
ASSIGN <<MyRegion//VIC>> True IF:<<Region//Other VIC>>
 `}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <Checkbox checked={isAutoRunEnabled} onChange={handleCheckboxChange}>
            Run this script automatically on report load
          </Checkbox>
          <Tooltip title="If checked, this script will execute after the data is initialized but before report elements load. The script saved here is the one last run manually using 'Run & Save Script'.">
            <span className="text-gray-400 cursor-help">
              <FontAwesomeIcon icon={faInfoCircle} />
            </span>
          </Tooltip>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Note: Ensure your script syntax is correct. Errors might affect
          subsequent operations.
        </p>
      </Modal>
    </>
  )
}

export default CustomQueryModal
